<template>
  <Dialog
    :dialog-title="$t('pptReport.dialogTitle')"
    max-width="440px"
    min-width="440px"
    :has-exit-button="true"
    :has-cancel-button="!isGenerating"
    :confirm-button-text="confirmButtonText"
    :is-button-disabled="!canDownload"
    :is-loading="isGenerating"
    @confirm="handleDownload"
    @cancel="handleClose"
  >
    <div class="flex flex-col items-center justify-center py-6 min-h-[120px]">
      <!-- Loading State -->
      <div v-if="isLoading" class="flex flex-col items-center gap-4">
        <div
          class="animate-spin rounded-full h-10 w-10 border-4 border-accent-200 border-t-accent-600"
        />
        <span class="text-sm text-accent-600">
          {{ progressText || $t("pptReport.preparingData") }}
        </span>
      </div>

      <!-- Error State -->
      <div
        v-else-if="hasError"
        class="flex flex-col items-center gap-4 text-center"
      >
        <div
          class="flex items-center justify-center h-10 w-10 rounded-full bg-danger-100"
        >
          <ClientOnly>
            <font-awesome-icon
              :icon="['fas', 'exclamation-triangle']"
              class="text-danger-600 text-lg"
            />
          </ClientOnly>
        </div>
        <div>
          <p class="text-sm font-medium text-danger-700">
            {{ $t("pptReport.errorTitle") }}
          </p>
          <p class="text-xs text-accent-500 mt-1">
            {{ errorMessage }}
          </p>
        </div>
        <button
          class="text-sm text-accent-600 hover:text-accent-800 underline"
          @click="retry"
        >
          {{ $t("pptReport.retry") }}
        </button>
      </div>

      <!-- Generating State -->
      <div v-else-if="isGenerating" class="flex flex-col items-center gap-4">
        <div
          class="animate-spin rounded-full h-10 w-10 border-4 border-secondary-200 border-t-secondary-600"
        />
        <span class="text-sm text-accent-600">
          {{ progressText || $t("pptReport.generating") }}
        </span>
      </div>

      <!-- Ready State -->
      <div v-else-if="isReady" class="flex flex-col items-center gap-4">
        <div
          class="flex items-center justify-center h-10 w-10 rounded-full bg-success-100"
        >
          <ClientOnly>
            <font-awesome-icon
              :icon="['fas', 'check']"
              class="text-success-600 text-lg"
            />
          </ClientOnly>
        </div>
        <div class="text-center">
          <p class="text-sm font-medium text-accent-900">
            {{ $t("pptReport.ready") }}
          </p>
          <p class="text-xs text-accent-500 mt-1">
            {{ reportTitle }}
          </p>
        </div>
      </div>
    </div>

    <!-- Hidden chart for PPT bubble chart export -->
    <div
      v-if="hasTopicsData && !bubbleChartCaptured"
      class="absolute -left-[9999px] w-[400px] h-[300px]"
    >
      <ClientOnly>
        <highcharts ref="bubbleChartRef" :options="bubbleChartOptions" />
      </ClientOnly>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import Dialog from "@/components/shared/custom-dialog/CustomDialog.vue";
import { usePptReportsStore } from "~/store/ppt-reports";
import { DEFAULT_CHART_COLORS } from "~/utils/ppt-reports/slides/ppt-defaults";

const { t } = useI18n();
const pptStore = usePptReportsStore();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "success"): void;
}>();

// Bubble chart for PPT export
const bubbleChartRef = ref<any>(null);
const bubbleChartCaptured = ref(false);

// Store state mappings
const isLoading = computed(() => pptStore.isLoading);
const isGenerating = computed(() => pptStore.isGenerating);
const isReady = computed(() => pptStore.isReady);
const hasError = computed(() => !!pptStore.error);
const errorMessage = computed(() => pptStore.error || "");
const reportTitle = computed(() => pptStore.reportSettings.title);

// Topics data for bubble chart
const hasTopicsData = computed(() => {
  const keywords = pptStore.reportData.chartData?.topicSearch?.keywords;
  return keywords && keywords.length > 0;
});

// Chart colors matching PPT slide legend (from ppt-defaults)
// These need to match what's used in trending-topics-slide.ts
const chartColors = DEFAULT_CHART_COLORS.map((c) => `#${c}`);

// Bubble chart options matching TopicsCharts.vue packedbubble config
const bubbleChartOptions = computed(() => {
  const keywords = pptStore.reportData.chartData?.topicSearch?.keywords || [];
  const topKeywords = keywords.slice(0, 10);

  const seriesData = topKeywords.map((keyword: any) => ({
    name: keyword.datasetName || keyword.name || "Topic",
    data: [
      {
        name: keyword.datasetName || keyword.name || "Topic",
        value: keyword.mentionTotalCount || 0,
      },
    ],
  }));

  return {
    chart: {
      type: "packedbubble",
      width: 400,
      height: 300,
      animation: false,
      events: {
        load() {
          // Capture chart after render
          nextTick(() => captureBubbleChart());
        },
      },
    },
    title: { text: null },
    credits: { enabled: false },
    colors: chartColors.slice(0, seriesData.length),
    legend: { enabled: false },
    tooltip: { enabled: false },
    plotOptions: {
      packedbubble: {
        // NOTE: zMin/zMax settings match CustomChart.vue (Topics page) intentionally.
        // Both have the same sizing limitation - fix together in future if needed.
        minSize: "50%",
        maxSize: "100%",
        zMin: 0,
        zMax: 1000,
        layoutAlgorithm: {
          splitSeries: false,
          gravitationalConstant: 0.02,
        },
        dataLabels: {
          enabled: true,
          style: {
            color: "black",
            textOutline: "none",
            fontWeight: "normal",
          },
          formatter(this: any) {
            const maxLength = 15;
            if (this.point.name.length > maxLength) {
              let wrappedText = "";
              const words = this.point.name.split(" ");
              let line = "";
              for (let i = 0; i < words.length; i++) {
                if ((line + words[i]).length > maxLength) {
                  wrappedText += line.trim() + "<br>";
                  line = "";
                }
                line += words[i] + " ";
              }
              wrappedText += line.trim();
              return wrappedText;
            }
            return this.point.name;
          },
        },
      },
    },
    series: seriesData,
  };
});

// Capture the bubble chart as base64 PNG for PPT export
const captureBubbleChart = async () => {
  if (bubbleChartCaptured.value || !bubbleChartRef.value?.chart) {
    return;
  }

  try {
    const chart = bubbleChartRef.value.chart;

    // Wait a bit for chart to fully render
    await new Promise((resolve) => setTimeout(resolve, 500));

    // Get SVG from Highcharts
    const svg = chart.getSVG({
      chart: { width: 400, height: 300 },
    });

    // Convert SVG to base64 PNG using canvas
    const canvas = document.createElement("canvas");
    canvas.width = 400;
    canvas.height = 300;
    const ctx = canvas.getContext("2d");

    if (ctx) {
      const img = new Image();
      const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);

      img.onload = () => {
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, 400, 300);
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);

        const base64 = canvas.toDataURL("image/png");
        pptStore.setTopicChartImage(base64);
        bubbleChartCaptured.value = true;
      };

      img.onerror = () => {
        console.warn("PPT Report: Failed to convert bubble chart SVG to PNG");
        URL.revokeObjectURL(url);
        bubbleChartCaptured.value = true; // Mark as captured to prevent retries
      };

      img.src = url;
    }
  } catch (error) {
    console.warn("PPT Report: Error capturing bubble chart:", error);
    bubbleChartCaptured.value = true;
  }
};

// Progress text from store
const progressText = computed(() => {
  if (pptStore.generationProgress.currentStep) {
    return pptStore.generationProgress.currentStep;
  }
  return "";
});

// Button states
const canDownload = computed(() => isReady.value && !isGenerating.value);

const confirmButtonText = computed(() => {
  if (isLoading.value) return t("pptReport.loading");
  if (isGenerating.value) return t("pptReport.generating");
  if (hasError.value) return t("pptReport.close");
  return t("pptReport.download");
});

// Actions
const handleDownload = async () => {
  if (hasError.value) {
    handleClose();
    return;
  }

  if (!canDownload.value) {
    return;
  }

  try {
    // Pass the t function for proper i18n in the generated PPT
    await pptStore.generatePowerPointReport(t);

    // Close dialog on success (no error after generation)
    if (!pptStore.error) {
      emit("success");
      handleClose();
    }
  } catch (error) {
    console.error("PptReportDialog: Exception in handleDownload:", error);
  }
};

const handleClose = () => {
  emit("close");
};

const retry = async () => {
  pptStore.clearError();
  await pptStore.fetchReportData();
};

// Fetch data on mount
onMounted(async () => {
  pptStore.reset();
  await pptStore.fetchReportData();
});

// Cleanup on unmount
onUnmounted(() => {
  // Don't reset store on unmount - allow background operations to complete
});
</script>
